import { logStartGreyError } from 'owa-analytics-start';
import getStore from '../store/store';
import { getOWAConnectedAccounts } from '../selectors';
import {
    getGlobalSettingsCoprincipalAccount,
    isAccountSourceListStoreInitialized,
    tryGetGlobalSettingsAccountMailboxInfo,
} from 'owa-account-source-list-store';
import type { AccountKey } from 'owa-client-types';

let currentLogCount = 0;
const maxLogCount = 3;

/**
 * Allow the tests to reset the log count
 */
export function resetLogCount(): void {
    currentLogCount = 0;
}

/**
 * To suppport shared/delegate mailboxes as coprincipal accounts we need to be able to support multiple
 * coprincipal accounts with the same user identity values (such as user@example.com and shared@example.com
 * that are both authenticated as user@example.com). This means that we need to replace the user identity
 * key in the connected accounts with the account key value of the mailbox info. This function converts the
 * account key or user identity value into an account key value.
 *
 * @param accountKeyOrUserIdentity Either an account key value or user identity.
 * @returns The account key value
 */
export function getAccountKeyForConnectedAccount(accountKeyOrUserIdentity: string): AccountKey {
    // If the account key is a persistence id we can then use the passed in account key value
    if (getStore().accountsMap.has(accountKeyOrUserIdentity as AccountKey)) {
        // The value is a valid account key value
        return accountKeyOrUserIdentity as AccountKey;
    }

    // The passed in value was not an account key for the connected account store, check if it is a user identity
    // by looking through the connected accounts and finding the first one that matches the user identity
    const matchingAccounts = getOWAConnectedAccounts().filter(
        account => account.userIdentity == accountKeyOrUserIdentity
    );

    if (matchingAccounts.length > 0) {
        // We found a matching account and can use the accountKey of the match. However in the case where
        // more than one account matches the user identity we may not have gotten the correct account. So we
        // log a grey error so that we can tell where the user identity is still being used.
        if (currentLogCount < maxLogCount) {
            ++currentLogCount;

            const errorForStack = new Error('UserIdentityUsedForConnectedAccount');

            logStartGreyError(
                'UserIdentityUsedForConnectedAccount',
                tryGetGlobalSettingsAccountMailboxInfo(),
                errorForStack,
                {
                    matches: matchingAccounts.length,
                }
            );
        }

        return matchingAccounts[0].accountKey as AccountKey;
    }

    // In OWA the global settings account will not be in the connected accounts and so we need
    // to check to see if we are the global settings account
    if (isAccountSourceListStoreInitialized()) {
        const globalSettingsAccount = getGlobalSettingsCoprincipalAccount();
        if (globalSettingsAccount.mailboxInfo.userIdentity == accountKeyOrUserIdentity) {
            return globalSettingsAccount.persistenceId as AccountKey;
        }
    }

    // Did not find a matching account key / user identity return the passed in value
    return accountKeyOrUserIdentity as AccountKey;
}
